// apps/admin/src/lib/permissions.ts
//
// Phase 8.1 Task 02 — Admin permission catalog.
// SEC-5: the client no longer maps role names to permissions; grants come from
// the backend `/auth/me/permissions` only.
// Phase 8.2 Task 03 — Extended with Finance/Catalog/User Mgmt/Support keys
//                     (colon-notation + amount-gate helpers).
//
// vg-binding: P8.D-132 — 3 admin role tiers + custom override (SuperAdmin =
//             ALL, Admin = all minus system settings + admin mgmt,
//             SupportAdmin = limited subset).
// vg-binding: P8.D-135 — Super Admin can create custom sub-roles from
//             Admin permission pool (consumed by /vg phase 8.3 UI; here we
//             expose the catalog and helpers so the pool is enumerable).
// vg-binding: P8.D-163 — Layout-level auth guard + per-route permission
//             check. The PERMISSIONS enum here is the source of truth for
//             route-guard wiring (F-006 lesson — every new admin route MUST
//             wire to AuthGuard).
// vg-binding: P8.D-208 — Hybrid 7-role superseded (single-role per user
//             DEPRECATED). This module reads `user.roles[]` array, not a
//             single `primaryRole`, to honour FOUNDATION.F-03 +.
// vg-binding: P8.D-253 — Support Admin CANNOT impersonate any portal.
//             `IMPERSONATE_*` permissions are removed from SUPPORT_ADMIN
//             grants and from the custom-role pool exposed to Super Admin.
// vg-binding: P8.D-486 — Kế toán (Accountant) role: dedicated subset of
//             finance perms (read + threshold-bound mutations + credit memo
//             apply). Mirrored from backend roles.seed.ts.
// vg-binding: P8.D-490 — Permission granularity: amount-gated keys
//             (`:up_to_X` / `:unlimited`) for credit-memo + invoice
//             mark-paid. Parsed via `hasAmountGate()`.
// vg-binding: P8.D-495 — RBAC namespace flattening: colon-notation keys
//             (`module:action[:variant]`) mirrors backend permissions
//             catalog (apps/api/src/modules/auth/permissions.catalog.ts).
//             Re-declared FE-side so the catalog is enumerable without a
//             server roundtrip (UX hint layer; backend re-checks).
//
// Catalog notation:
//   - Colon-segmented machine keys (mirrors backend pattern in
//     apps/api/src/modules/auth/middlewares/permission.middleware.ts).
//   - Wildcard match handled BE-side via `permissionMatches`; FE compares
//     literal grant against requested key.
//   - Catalog kept narrow for Phase 8.1 — adjacent sub-phases (8.2 finance,
//     8.3 system) will extend the same enum without renaming existing keys.
//   - Phase 8.2 keys append to the enum below; existing keys
//     (MANAGE_LEGAL_ENTITY, CREATE_PAYMENT_REQUEST, VIEW_FINANCIAL_COST)
//     are kept as-is to preserve route-guard wiring from Phase 8.1.

/**
 * Tier-1 global role identifiers used by the admin SPA. Mirrors
 * FOUNDATION.F-03 hybrid model: each user carries an array of these on
 * `user.roles[]`. superseded the single-role assumption.
 */
export const ADMIN_GLOBAL_ROLES = {
  SUPERADMIN: 'superadmin',
  ADMIN: 'admin',
  SUPPORT: 'support',
  // kế toán (Accountant). Added by Phase 8.2 task 03 to mirror
  // the backend roles.seed.ts. Backend `roleType: 'accountant'`.
  ACCOUNTANT: 'accountant',
} as const;

/**
 * Admin permission keys — exhaustive list scoped to Phase 8.1 surfaces.
 * Other sub-phases (8.2/8.3) may add keys; never rename or remove.
 */
export const PERMISSIONS = {
  // -------------------------------------------------------------------------
  // Dashboard & navigation (....)
  // -------------------------------------------------------------------------
  VIEW_DASHBOARD: 'dashboard:view',
  VIEW_DASHBOARD_FULL_KPIS: 'dashboard:view_full_kpis',

  // -------------------------------------------------------------------------
  // Orders (.. — cost-side gate)
  // -------------------------------------------------------------------------
  VIEW_ORDERS: 'orders:view',
  VIEW_ORDER_DETAIL: 'orders:view_detail',
  VIEW_FINANCIAL_COST: 'orders:view_financial_cost',
  ASSIGN_ORDER: 'orders:assign',
  CANCEL_ORDER: 'orders:cancel',
  REFUND_ORDER: 'orders:refund',
  MANAGE_ASSIGNMENT_RULES: 'orders:manage_assignment_rules',

  // -------------------------------------------------------------------------
  // Vendor management (..)
  // -------------------------------------------------------------------------
  VIEW_VENDORS: 'vendors:view',
  MANAGE_VENDORS: 'vendors:manage',
  APPROVE_VENDOR_KYC: 'vendors:approve_kyc',
  IMPERSONATE_VENDOR: 'vendors:impersonate',

  // -------------------------------------------------------------------------
  // Merchant management (..)
  // -------------------------------------------------------------------------
  VIEW_MERCHANTS: 'merchants:view',
  MANAGE_MERCHANTS: 'merchants:manage',
  SUSPEND_MERCHANT: 'merchants:suspend',
  IMPERSONATE_MERCHANT: 'merchants:impersonate',

  // -------------------------------------------------------------------------
  // Stores (..)
  // -------------------------------------------------------------------------
  VIEW_STORES: 'stores:view',
  MANAGE_STORES: 'stores:manage',

  // -------------------------------------------------------------------------
  // Support (..)
  // -------------------------------------------------------------------------
  VIEW_SUPPORT_TICKETS: 'support:view',
  RESPOND_SUPPORT_TICKETS: 'support:respond',
  ESCALATE_SUPPORT_TICKET: 'support:escalate',

  // -------------------------------------------------------------------------
  // Finance (consumer — full ownership in 8.2; 8.1 anchors only)
  // -------------------------------------------------------------------------
  VIEW_FINANCE: 'finance:view',
  CREATE_PAYMENT_REQUEST: 'finance:create_payment_request',
  MANAGE_LEGAL_ENTITY: 'finance:manage_legal_entity',

  // -------------------------------------------------------------------------
  // System (8.3 ownership — listed for completeness; SuperAdmin only)
  // -------------------------------------------------------------------------
  VIEW_SYSTEM_SETTINGS: 'system:view',
  MANAGE_SYSTEM_SETTINGS: 'system:manage',
  MANAGE_ADMIN_USERS: 'system:manage_admins',
  MANAGE_FEATURE_FLAGS: 'system:manage_feature_flags',

  // -------------------------------------------------------------------------
  // Impersonation — Super-Admin specific (: Support Admin denied)
  // -------------------------------------------------------------------------
  IMPERSONATE_SUPERADMIN: 'impersonate:superadmin',

  // =========================================================================
  // Phase 8.2 — Finance / Catalog / User Mgmt / Support extensions.
  // Colon-notation keys; amount-gated keys.
  // Mirrors backend permissions.catalog.ts (task 01).
  // =========================================================================

  // -------------------------------------------------------------------------
  // Finance — Credit memo (amount gate; kế toán subset).
  // -------------------------------------------------------------------------
  CREDIT_MEMO_READ: 'credit_memo:read',
  CREDIT_MEMO_ISSUE_UP_TO_500: 'credit_memo:issue:up_to_500',
  CREDIT_MEMO_ISSUE_UNLIMITED: 'credit_memo:issue:unlimited',
  CREDIT_MEMO_APPROVE: 'credit_memo:approve',
  CREDIT_MEMO_APPLY: 'credit_memo:apply',

  // -------------------------------------------------------------------------
  // Finance — Invoice (amount gate on mark_paid).
  // -------------------------------------------------------------------------
  INVOICE_READ: 'invoice:read',
  INVOICE_MARK_PAID_UP_TO_10K: 'invoice:mark_paid:up_to_10k',
  INVOICE_MARK_PAID_UNLIMITED: 'invoice:mark_paid:unlimited',
  INVOICE_PAYMENT_REVERSE: 'invoice:payment_reverse',
  INVOICE_RESEND_EMAIL: 'invoice:resend_email',
  INVOICE_MANUAL_GENERATE: 'invoice:manual_generate',

  // -------------------------------------------------------------------------
  // Finance — Refund tasks + aging + legal entity (finance ops).
  // -------------------------------------------------------------------------
  REFUND_TASK_READ: 'refund_task:read',
  REFUND_TASK_COMPLETE: 'refund_task:complete',
  AGING_REPORT_VIEW: 'aging_report:view',
  LEGAL_ENTITY_READ: 'legal_entity:read',
  LEGAL_ENTITY_WRITE: 'legal_entity:write',

  // -------------------------------------------------------------------------
  // Finance — Billing ops (wallet handover sweep b11).
  // Keys mirror what the BE actually enforces via requirePermission today
  // (withdraw-queue, topup, chargebacks, fraud config, transfer groups,
  // billing settings pages, admin credits). Seeded to superadmin+admin in
  // permissions.seed.ts. Do NOT add FE-only keys here — a key the BE never
  // grants would hide the menu from every admin.
  // -------------------------------------------------------------------------
  BILLING_WITHDRAW: 'billing:withdraw',
  BILLING_TOPUP_MANUAL: 'billing:topup_manual',
  BILLING_CHARGEBACK: 'billing:chargeback',
  BILLING_FRAUD_CONFIG: 'billing:fraud_config',
  BILLING_TRANSFER_GROUP_MANAGE: 'billing:transfer_group_manage',
  BILLING_SETTINGS_MANAGE: 'billing:billing_settings_manage',
  CREDIT_READ: 'credit:read',

  // -------------------------------------------------------------------------
  // Catalog — Pricing + product management.
  // MANAGE_PRICING is the umbrella admin gate; PRODUCTS_*_MANAGE keys
  // grant section-specific access for the catalog tree UI.
  // -------------------------------------------------------------------------
  MANAGE_PRICING: 'pricing:manage',
  // Phase 4.7 Task 19 — Shipping Rate Engine v2 admin gate.
  // Umbrella permission for the `/shipping` surface (Fee / Providers /
  // Weight Bands / Methods tabs). Cost-sensitive tabs (Rate / Margin /
  // History) additionally require VIEW_FINANCIAL_COST.
  // Mirrors the BE shipping permissions catalog
  // (apps/api/src/modules/shipping/* permissions seed).
  // vg-binding: CONTEXT:decisions=P4.7.D-21
  MANAGE_SHIPPING: 'shipping:manage',
  MANAGE_PRODUCT_SPECIAL: 'product:special:manage',
  PRODUCTS_TEMPLATE_MANAGE: 'products:template:manage',
  PRODUCTS_CATEGORY_MANAGE: 'products:category:manage',
  PRODUCTS_BLUEPRINT_MANAGE: 'products:blueprint:manage',
  PRODUCTS_BUNDLE_MANAGE: 'products:bundle:manage',
  PRODUCTS_MAPPING_MANAGE: 'products:mapping:manage',
  PRODUCTS_COLLECTION_MANAGE: 'products:collection:manage',
  PRODUCTS_TECHNIQUE_MANAGE: 'products:technique:manage',
  // Phase 8.5 — Artwork Doctor catalog pages.
  PRODUCTS_MATERIAL_MANAGE: 'products:material:manage',
  PRODUCTS_REGION_ERROR_MANAGE: 'products:region_error:manage',
  PRODUCTS_CHECK_TYPE_MANAGE: 'products:check_type:manage',
  // Phase 8.4 Amendment #2 — Print Area catalog page.
  // vg-binding: CONTEXT:P8.4.D-51
  PRODUCTS_PRINT_AREA_MANAGE: 'products:print_area:manage',
  PRODUCTS_ADDON_MANAGE: 'products:addon:manage',
  PRODUCTS_APPROVAL_MANAGE: 'products:approval:manage',
  // dbg-wizard-iter4 (promote) — Options entity admin page key.
  // BE uses `option:manage` from PHASE_2_CATALOG_PERMISSIONS (admin role
  // inherits via PHASE_2_ROLE_BINDINGS; superadmin via *:* wildcard).
  PRODUCTS_OPTION_MANAGE: 'option:manage',

  // -------------------------------------------------------------------------
  // Support — Ticket lifecycle (; replaces P8.1 generic support:* set
  // when the new tree UI lands in Wave 6+. Keep generic keys for backward
  // compat with Phase 8.1 routes).
  // -------------------------------------------------------------------------
  SUPPORT_TICKET_READ: 'support:ticket:read',
  SUPPORT_TICKET_REPLY: 'support:ticket:reply',
  SUPPORT_TICKET_ESCALATE: 'support:ticket:escalate',
  SUPPORT_TICKET_CLOSE: 'support:ticket:close',
  SUPPORT_TICKET_ASSIGN: 'support:ticket:assign',

  // -------------------------------------------------------------------------
  // User management (; Super-Admin default per task-01 spec).
  // -------------------------------------------------------------------------
  USER_MANAGE: 'user:manage',

  // -------------------------------------------------------------------------
  // Phase 8.3 — Admin CMS & System surfaces.
  // Added 2026-05-18 via /vg:debug 8.3 Cluster A so the sidebar can gate the
  // Reports / HomeManagement / Inventory / 8.3 System / Monitoring items on
  // colon-notation keys. Keys mirror the BE seed
  // catalog from `apps/api/src/modules/auth/seed/permissions.seed.ts`.
  //
  // vg-binding: CONTEXT:decisions=P8.D-132,P8.D-135,P8.D-382,P8.D-475
  // -------------------------------------------------------------------------

  // CMS (single permission gates all 10 sub-modules).
  HOME_MANAGEMENT_MANAGE: 'home_management:manage',

  // System (SA-only items carry the `saOnly` flag in groups.ts).
  SYSTEM_SETTINGS_READ: 'system:settings:read',
  SYSTEM_SETTINGS_WRITE_NOTIFICATIONS: 'system:settings:write:notifications',
  SYSTEM_HEALTH: 'system:health',
  SYSTEM_MAINTENANCE_MODE: 'system:maintenance_mode',
  SYSTEM_EMERGENCY_MODE: 'system:emergency_mode',
  SYSTEM_BUG_TRACKER: 'system:bug_tracker',

  // Email templates + announcements (Phase 8.3 Wave 6 task-44 / task-14).
  EMAIL_TEMPLATE_READ: 'email_template:read',
  EMAIL_TEMPLATE_WRITE: 'email_template:write',
  ANNOUNCEMENT_WRITE: 'announcement:write',

  // Approvals + audit + monitoring + inventory.
  DUAL_APPROVAL_VOTE: 'dual_approval:vote',
  AUDIT_READ: 'audit:read',
  MONITORING_READ: 'monitoring:read',
  INVENTORY_READ: 'inventory:read',

  // -------------------------------------------------------------------------
  // Phase 4.11 — Tracking ingest force-refresh gate.
  // `tracking:manage` gates the per-package force-refresh action
  // (RefreshTrackingButton, Task 19). Mirrors the BE perm seeded in
  // apps/api/src/scripts/seed-admin-role-permissions.ts (co-located with the
  // TRACKING_ERROR_CODES table). Server-side authz is the real gate
  // (P4.11.D-28); the FE only hides/disables the button as a UX hint —
  // SUPERADMIN (`*:*`) + ADMIN inherit it through the runtime permission set
  // returned by the backend.
  //
  // vg-binding: CONTEXT:decisions=P4.11.D-26,P4.11.D-27,P4.11.D-28
  // vg-binding: INTERFACE-STANDARDS:error-shape
  // -------------------------------------------------------------------------
  MANAGE_TRACKING: 'tracking:manage',
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

// ===========================================================================
// Phase 8.2 helpers — amount-gate parsing + permission tree shaping.
// ===========================================================================

/**
 * Parsed amount-gate descriptor returned by {@link parseAmountGate}.
 *
 * - `unlimited: true` — key suffix is `:unlimited` (no cap).
 * - `unlimited: false` + `cap` — key suffix encodes a numeric cap
 *   (`:up_to_500` → cap=500, `:up_to_10k` → cap=10000, `:up_to_1m` →
 *   cap=1_000_000). Suffix units: bare = USD; `k` = thousand; `m` = million.
 */
export interface AmountGate {
  /** True when the key suffix is exactly `:unlimited`. */
  unlimited: boolean;
  /** Numeric cap in the same unit as the requested amount. `null` when unlimited. */
  cap: number | null;
  /** The raw suffix segment (e.g. `up_to_10k`, `unlimited`). */
  suffix: string;
}

/**
 * Parse the amount-gate suffix from a colon-notation permission key.
 *
 * Returns `null` when the key has no amount-gate suffix (i.e. is not an
 * `:up_to_X` or `:unlimited` key). Callers wanting a boolean gate-check
 * should use {@link hasAmountGate} instead.
 *
 * Examples:
 *   parseAmountGate('credit_memo:issue:up_to_500') → { unlimited: false, cap: 500, suffix: 'up_to_500' }
 *   parseAmountGate('invoice:mark_paid:up_to_10k') → { unlimited: false, cap: 10000, suffix: 'up_to_10k' }
 *   parseAmountGate('credit_memo:issue:unlimited') → { unlimited: true, cap: null, suffix: 'unlimited' }
 *   parseAmountGate('invoice:read')                → null
 *   parseAmountGate('credit_memo:issue:up_to_xyz') → null  (malformed suffix)
 */
export function parseAmountGate(key: string): AmountGate | null {
  const parts = key.split(':');
  if (parts.length < 2) return null;
  const suffix = parts[parts.length - 1] ?? '';
  if (suffix === 'unlimited') {
    return { unlimited: true, cap: null, suffix };
  }
  if (!suffix.startsWith('up_to_')) return null;
  const rawAmount = suffix.slice('up_to_'.length); // e.g. "500", "10k", "1m"
  const cap = parseAmountSuffix(rawAmount);
  if (cap == null) return null;
  return { unlimited: false, cap, suffix };
}

/**
 * Internal — parse the numeric portion of an `up_to_X` suffix.
 * Supports bare integers, `k` (×1_000), and `m` (×1_000_000). Returns
 * `null` on malformed input.
 */
function parseAmountSuffix(raw: string): number | null {
  if (raw.length === 0) return null;
  const lastChar = raw[raw.length - 1] ?? '';
  let multiplier = 1;
  let numericPart = raw;
  if (lastChar === 'k') {
    multiplier = 1_000;
    numericPart = raw.slice(0, -1);
  } else if (lastChar === 'm') {
    multiplier = 1_000_000;
    numericPart = raw.slice(0, -1);
  }
  if (!/^\d+$/.test(numericPart)) return null;
  const base = Number.parseInt(numericPart, 10);
  if (!Number.isFinite(base)) return null;
  return base * multiplier;
}

/**
 * True iff the permission `key` grants `amount` within its amount-gate.
 *
 * - `:unlimited` suffix → always true (any positive amount).
 * - `:up_to_X` suffix → true when `amount <= X`.
 * - No amount-gate suffix → returns `false` (non-amount keys cannot gate
 *   monetary actions; caller should use {@link hasPermission} from rbac.ts).
 *
 * Consumers (Wave 6+ RBAC management UI, finance form submit-guards):
 *   if (hasAmountGate(grant, draft.amount)) { …allow… }
 *
 * Note: this is a pure parser over the KEY suffix; it does NOT check
 * whether the user actually holds the grant. Always combine with
 * `hasPermission(user, perms, key)` from rbac.ts before allowing the
 * action.
 */
export function hasAmountGate(key: string, amount: number): boolean {
  const gate = parseAmountGate(key);
  if (gate == null) return false;
  if (gate.unlimited) return true;
  if (gate.cap == null) return false;
  return amount <= gate.cap;
}

/**
 * Node in the permission tree produced by {@link parsePermissionTree}.
 * Children are keyed by the next colon-segment. Leaves carry the full
 * permission key for direct grant-binding from the RBAC tree UI.
 */
export interface PermissionTreeNode {
  /** The segment label at this depth (e.g. `credit_memo`, `issue`, `up_to_500`). */
  segment: string;
  /** Full colon-key when this node is a terminal leaf, else `null`. */
  fullKey: string | null;
  /** Child segments, sorted alphabetically for stable RBAC tree rendering. */
  children: PermissionTreeNode[];
}

/**
 * Group permission keys into a colon-prefix tree for the RBAC management
 * tree UI (Wave 6). Each segment of `module:action[:variant]` becomes a
 * tree node; terminal leaves carry the full key so the UI can bind the
 * grant directly without re-joining.
 *
 * Example:
 *   parsePermissionTree([
 *     'credit_memo:read',
 *     'credit_memo:issue:up_to_500',
 *     'credit_memo:issue:unlimited',
 *     'invoice:read',
 *   ])
 *   →
 *   [
 *     { segment: 'credit_memo', fullKey: null, children: [
 *       { segment: 'issue', fullKey: null, children: [
 *         { segment: 'unlimited',  fullKey: 'credit_memo:issue:unlimited',  children: [] },
 *         { segment: 'up_to_500',  fullKey: 'credit_memo:issue:up_to_500',  children: [] },
 *       ]},
 *       { segment: 'read', fullKey: 'credit_memo:read', children: [] },
 *     ]},
 *     { segment: 'invoice', fullKey: null, children: [
 *       { segment: 'read', fullKey: 'invoice:read', children: [] },
 *     ]},
 *   ]
 *
 * Behaviour notes:
 * - Roots returned alphabetically.
 * - Each level sorted alphabetically.
 * - Duplicate keys collapse to a single leaf (deduplicates by full key).
 * - Keys missing a colon (e.g. legacy SCREAMING_SNAKE constants like
 *   `IMPERSONATE_SUPERADMIN`) ARE included as single-segment leaves so the
 *   tree UI can still grant them.
 */
export function parsePermissionTree(
  keys: ReadonlyArray<string>,
): PermissionTreeNode[] {
  // Working tree: map of segment → node + children map for O(1) lookup
  // while building.
  interface WipNode {
    segment: string;
    fullKey: string | null;
    children: Map<string, WipNode>;
  }

  const roots = new Map<string, WipNode>();
  const seenKeys = new Set<string>();

  for (const key of keys) {
    if (typeof key !== 'string' || key.length === 0) continue;
    if (seenKeys.has(key)) continue;
    seenKeys.add(key);

    const parts = key.split(':').filter((p) => p.length > 0);
    if (parts.length === 0) continue;

    let parentChildren = roots;
    for (let i = 0; i < parts.length; i++) {
      const segment = parts[i] as string;
      const isLeaf = i === parts.length - 1;
      let node = parentChildren.get(segment);
      if (node == null) {
        node = { segment, fullKey: null, children: new Map() };
        parentChildren.set(segment, node);
      }
      if (isLeaf) {
        // Assigning fullKey at the leaf is idempotent — dedup above ensures
        // a single visit per key, so we never overwrite a different value.
        node.fullKey = key;
      }
      parentChildren = node.children;
    }
  }

  // Materialise the WIP tree into the public shape with stable alphabetic
  // ordering at every level.
  function materialise(node: WipNode): PermissionTreeNode {
    const children = Array.from(node.children.values())
      .map(materialise)
      .sort((a, b) => a.segment.localeCompare(b.segment));
    return { segment: node.segment, fullKey: node.fullKey, children };
  }

  return Array.from(roots.values())
    .map(materialise)
    .sort((a, b) => a.segment.localeCompare(b.segment));
}
